/*
 * Lazy tabs for the Featured Products carousel (sections/featured-products.liquid).
 *
 * Tabs other than the first are server-rendered as placeholders carrying the
 * collection handle ([data-lazy-tab]). Once a carousel comes within 800px of
 * the viewport, every lazy tab's cards are prefetched through the Section
 * Rendering API (sections/api-featured-tab.liquid) and kept in memory; the
 * first tap on a tab swaps the placeholders for the cards, usually instantly.
 * A tap before the prefetch lands shows the placeholders until it does.
 *
 * A tab set to show Shopify's recommendations on a product page also carries
 * the recommendations route and the product id ([data-lazy-recs-product]): its
 * cards come from Shopify's related products for that product, topped up from
 * the tab's collection if there are fewer than the tab's limit. When it is the
 * tab showing first ([data-lazy-auto]) it fills as soon as the carousel nears
 * the viewport rather than on a tap.
 *
 * Card images are given native lazy loading and a sizes value (the theme's
 * lazyload library only scans the DOM on initial load).
 */
(function () {
  // The section emits this script once per carousel; run once per page.
  if (window.__scFeaturedTabsLazy) return;
  window.__scFeaturedTabsLazy = true;

  var CARD_SIZES = '(min-width: 1280px) 20vw, (min-width: 1024px) 25vw, (min-width: 640px) 33vw, 44vw';
  var NO_HOVER = false;
  try { NO_HOVER = window.matchMedia && window.matchMedia('(hover: none)').matches; } catch (e) {}
  var cache = Object.create(null);

  function queryString(extra) {
    var params = ['section_id=api-featured-tab'].concat(extra || []);
    var country = window.Shopify && window.Shopify.country;
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (country) params.push('country=' + encodeURIComponent(country));
    if (currency) params.push('currency=' + encodeURIComponent(currency));
    var previewId;
    try { previewId = new URLSearchParams(window.location.search).get('preview_theme_id'); } catch (e) {}
    if (previewId) params.push('preview_theme_id=' + encodeURIComponent(previewId));
    return '?' + params.join('&');
  }

  function nativeLazyImages(root) {
    var imgs = root.querySelectorAll('img.lazyload, img[data-src], img[data-srcset]');
    Array.prototype.forEach.call(imgs, function (img) {
      if (NO_HOVER && img.classList.contains('second-image')) { img.parentNode.removeChild(img); return; }
      var src = img.getAttribute('data-src');
      var srcset = img.getAttribute('data-srcset');
      if (src) img.setAttribute('src', src);
      if (srcset) {
        img.setAttribute('srcset', srcset);
        if (!img.getAttribute('sizes') || img.getAttribute('data-sizes') === 'auto') img.setAttribute('sizes', CARD_SIZES);
      }
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-sizes');
      img.removeAttribute('data-widths');
      img.classList.remove('lazyload');
      if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy');
      if (!img.getAttribute('decoding')) img.setAttribute('decoding', 'async');
    });
  }

  // Fetch a URL's cards once; concurrent and later callers share the promise.
  function fetchCards(url) {
    if (cache[url]) return cache[url];
    cache[url] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return null;
        var holder = document.createElement('div');
        holder.innerHTML = html;
        return Array.prototype.slice.call(holder.querySelectorAll('[data-carousel-card]'));
      })
      .catch(function () { delete cache[url]; return null; });
    return cache[url];
  }

  function fetchTab(handle) {
    if (!handle) return Promise.resolve(null);
    return fetchCards('/collections/' + encodeURIComponent(handle) + queryString());
  }

  // Shopify's related products for a product page tab (at most 10).
  function fetchRecs(container) {
    var productId = container.getAttribute('data-lazy-recs-product');
    var route = container.getAttribute('data-lazy-recs-url');
    if (!productId || !route) return Promise.resolve(null);
    var limit = Math.min(parseInt(container.getAttribute('data-lazy-limit'), 10) || 10, 10);
    return fetchCards(route + queryString([
      'product_id=' + encodeURIComponent(productId),
      'limit=' + limit,
      'intent=related',
    ]));
  }

  function tabLimit(container) {
    return parseInt(container.getAttribute('data-lazy-limit'), 10) || 12;
  }

  // The cards a tab can show, in order, up to its limit: no repeats, not the
  // product being viewed, and no sold-out products if the section hides them.
  function pickCards(container, cards) {
    var limit = tabLimit(container);
    var hideSoldOut = container.getAttribute('data-lazy-hide-sold-out') === 'true';
    var seen = Object.create(null);
    var self = container.getAttribute('data-lazy-recs-product');
    if (self) seen[self] = true;
    var picked = [];
    (cards || []).forEach(function (card) {
      if (picked.length >= limit) return;
      if (hideSoldOut && card.getAttribute('data-available') === 'false') return;
      var id = card.getAttribute('data-product-id');
      if (id && seen[id]) return;
      if (id) seen[id] = true;
      picked.push(card);
    });
    return picked;
  }

  // A tab's cards: its collection's, or for a recommendations tab Shopify's
  // picks, topped up from the collection if too few of them can be shown.
  function tabCards(container) {
    var handle = container.getAttribute('data-lazy-collection');
    if (!container.hasAttribute('data-lazy-recs-product')) {
      return fetchTab(handle).then(function (cards) { return pickCards(container, cards); });
    }
    return fetchRecs(container).then(function (recs) {
      var picked = pickCards(container, recs);
      if (picked.length >= tabLimit(container)) return picked;
      return fetchTab(handle).then(function (more) { return pickCards(container, picked.concat(more || [])); });
    });
  }

  // Theme JS, Alpine and the wishlist app bind on DOMContentLoaded only; kick
  // the same re-init paths for injected cards.
  function reinit(root) {
    try { if (window.Alpine && typeof window.Alpine.initTree === 'function') window.Alpine.initTree(root); } catch (e) {}
    var section = root.closest && root.closest('.shopify-section');
    try {
      (section || root).dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: section && section.id ? section.id.replace('shopify-section-', '') : '' },
      }));
    } catch (e) {}
  }

  function fill(container) {
    var state = container.getAttribute('data-lazy-state');
    if (state === 'loading' || state === 'done') return;
    container.setAttribute('data-lazy-state', 'loading');
    tabCards(container).then(function (cards) {
      if (!cards.length) { container.setAttribute('data-lazy-state', 'error'); return; }
      var frag = document.createDocumentFragment();
      cards.forEach(function (card) { frag.appendChild(card.cloneNode(true)); });
      nativeLazyImages(frag);
      container.innerHTML = '';
      container.appendChild(frag);
      container.setAttribute('data-lazy-state', 'done');
      reinit(container);
    });
  }

  // Near the viewport: fill the tab showing first if it waits on a fetch, and
  // prefetch the rest.
  function prefetchSection(section) {
    Array.prototype.forEach.call(section.querySelectorAll('[data-lazy-tab]'), function (c) {
      if (c.hasAttribute('data-lazy-auto')) fill(c);
      else tabCards(c);
    });
  }

  function run() {
    var containers = document.querySelectorAll('[data-lazy-tab]');
    if (!containers.length) return;

    // Swap on the first tap of a tab button in the same section.
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('.tab-button[data-tab-index]');
      if (!btn) return;
      var section = btn.closest('.shopify-section') || document;
      var target = section.querySelector('[data-lazy-tab][data-lazy-index="' + btn.getAttribute('data-tab-index') + '"]');
      if (target) fill(target);
    }, true);

    // Prefetch every lazy tab once its carousel is within 800px of the viewport.
    var sections = [];
    Array.prototype.forEach.call(containers, function (c) {
      var s = c.closest('.shopify-section');
      if (s && sections.indexOf(s) === -1) sections.push(s);
    });
    if (!('IntersectionObserver' in window)) { sections.forEach(prefetchSection); return; }
    var io = new IntersectionObserver(function (records) {
      records.forEach(function (r) {
        if (!r.isIntersecting) return;
        io.unobserve(r.target);
        prefetchSection(r.target);
      });
    }, { rootMargin: '800px 0px' });
    sections.forEach(function (s) { io.observe(s); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
})();
